import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SimplePopover } from '@dev/ee-components';
import { truncate, map } from 'lodash-es';

import { ContactStatus } from '@dev/ee-api/ee-api-models';
import { getAccountStore } from '@/store/account/actions';
import { getListstStore } from '@/store/lists/actions';
import { useDashboardElementVisibility } from '@/services/hooks/useDashboardElementVisibility';
import {
    ListDetailsProps,
    ALL_CONTACTS_LIST,
    ALL_CONTACTS_LIST_NAME,
    ContactStatusesProps,
    ContactListsProps,
    AddContactParams,
} from '../AddInterface';
import { InputContainer } from './StyledComponents';
import { contactsStatuses } from '../config/getContactStatuses';

export const ListDetails = (props: ListDetailsProps): JSX.Element => {
    const { postContactsParams, listName, segmentName, setAddContactParam } = props;
    const { t } = useTranslation();
    const account = useSelector(getAccountStore);
    const lists = useSelector(getListstStore);
    const {
        audience: { isAudienceTransactionalContactStatusVisible },
    } = useDashboardElementVisibility();
    const name =
        postContactsParams.listName === ALL_CONTACTS_LIST ? t(ALL_CONTACTS_LIST_NAME) : postContactsParams.listName;

    const getContactsStatuses = (): ContactStatusesProps[] =>
        contactsStatuses
            .filter((status) => {
                if (status.value === ContactStatus.Transactional) {
                    return isAudienceTransactionalContactStatusVisible;
                }
                return true;
            })
            .map((status) => ({
                value: status.value,
                label: t(status.label),
            }));

    const getContactLists = (): ContactListsProps[] => {
        const listsOptions: ContactListsProps[] =
            account?.GeneralInfo?.EnableContactFeatures && lists.collection
                ? map(lists.collection, (item) => {
                      const paidSubscription = item.Type === 'PaidSubscription';
                      if (paidSubscription) return null;
                      return {
                          value: item.ListName,
                          label: truncate(item.ListName, { length: 40, omission: '...' }),
                      };
                  }).filter(Boolean)
                : [];

        return [
            {
                label: t(ALL_CONTACTS_LIST_NAME),
                value: ALL_CONTACTS_LIST_NAME,
            },
            ...listsOptions,
        ];
    };

    const setContactList = (value: string): void => {
        const list = value === ALL_CONTACTS_LIST_NAME ? ALL_CONTACTS_LIST : value;
        setAddContactParam(AddContactParams.ListName, list);
    };

    const contactListsOptions = getContactLists();

    return (
        <div>
            <InputContainer>
                <SimplePopover
                    id="add-contacts-to-list"
                    disabled={!!listName || !!segmentName}
                    options={contactListsOptions}
                    label={t('contactlist')}
                    type="input"
                    value={postContactsParams.listName === ALL_CONTACTS_LIST ? contactListsOptions[0].value : name}
                    maxHeight={300}
                    onItemClick={(e, obj): void => {
                        setContactList(obj.value as string);
                    }}
                />
            </InputContainer>
            <InputContainer>
                <SimplePopover
                    id="add-contacts-select-status"
                    options={getContactsStatuses()}
                    label={t('contactstatus')}
                    type="input"
                    value={postContactsParams.status || ContactStatus.Active}
                    onItemClick={(e, obj): void => {
                        setAddContactParam(AddContactParams.Status, obj.value);
                    }}
                />
            </InputContainer>
        </div>
    );
};
